import { Form } from 'radix-ui'
import {
  AlertDialog,
  Button,
  Callout,
  Container,
  Flex,
  IconButton,
  RadioGroup,
  Section,
  Select,
  Text,
  TextArea,
  TextField,
} from '@radix-ui/themes'
import { TrashIcon } from '@radix-ui/react-icons'
import { FileUploader, FileWithPreview } from '@Components/common/file-uploader'
import { useMutation, useQuery } from '@tanstack/react-query'
import { PushNotificationServiceAPI } from '@API/data-services/push-notification-service'
import { BulkSendPushNotificationRequest, Platform } from '@Generated/back-office-client/src'
import { RootState } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { AudienceMode } from './types'

const TITLE_MAX = 120
const BODY_MAX = 1000

type MetadataRow = { id: string; key: string; value: string }

export const BulkPushNotifications = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)

  const [idempotencyKey, setIdempotencyKey] = useState(() => uuidv4())
  const [category, setCategory] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audienceMode, setAudienceMode] = useState<AudienceMode>(AudienceMode.ALL)
  const [csvFile, setCsvFile] = useState<FileWithPreview>()
  const [metadataRows, setMetadataRows] = useState<MetadataRow[]>([])
  const [formError, setFormError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingRequest, setPendingRequest] = useState<BulkSendPushNotificationRequest | null>(null)

  const { data: categoriesResp } = useQuery({
    queryKey: ['push-notification-categories', activeCustomerId],
    queryFn: () => PushNotificationServiceAPI.listCategories(activeCustomerId!),
    enabled: !!activeCustomerId,
  })

  const {
    mutate,
    isPending,
    error,
    data: result,
  } = useMutation({
    mutationFn: (request: BulkSendPushNotificationRequest) =>
      PushNotificationServiceAPI.bulkSend(activeCustomerId!, request),
    onSuccess: () => {
      setIdempotencyKey(uuidv4())
    },
  })

  const updateRow = (id: string, updates: Partial<MetadataRow>) => {
    setMetadataRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...updates } : r)))
  }

  const buildRequest = async (): Promise<BulkSendPushNotificationRequest | null> => {
    if (!category) {
      setFormError('Select a category.')
      return null
    }
    if (!title.trim() || !body.trim()) {
      setFormError('Title and body are required.')
      return null
    }

    const keys = metadataRows.map((r) => r.key.trim())
    if (keys.some((k) => !k)) {
      setFormError('Metadata keys cannot be empty.')
      return null
    }
    if (new Set(keys).size !== keys.length) {
      setFormError('Metadata keys must be unique.')
      return null
    }
    const data =
      metadataRows.length > 0
        ? Object.fromEntries(metadataRows.map((r) => [r.key.trim(), r.value]))
        : undefined

    let audience: Platform | undefined
    let recipientLookupIds: string[] | undefined
    if (audienceMode === AudienceMode.IOS) {
      audience = Platform.ios
    } else if (audienceMode === AudienceMode.ANDROID) {
      audience = Platform.android
    } else if (audienceMode === AudienceMode.CSV) {
      if (!csvFile) {
        setFormError('Upload a CSV of player lookup IDs.')
        return null
      }
      const text = await csvFile.file.text()
      recipientLookupIds = text
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
      if (recipientLookupIds.length === 0) {
        setFormError('The uploaded CSV is empty.')
        return null
      }
    }

    return { id: idempotencyKey, category, title, body, data, audience, recipientLookupIds }
  }

  const onReviewSend = async () => {
    setFormError(null)
    const request = await buildRequest()
    if (request) {
      setPendingRequest(request)
      setConfirmOpen(true)
    }
  }

  const audienceLabel = () => {
    if (audienceMode === AudienceMode.ALL) {
      return 'all users'
    }
    if (audienceMode === AudienceMode.IOS) {
      return 'all iOS users'
    }
    if (audienceMode === AudienceMode.ANDROID) {
      return 'all Android users'
    }
    return `${pendingRequest?.recipientLookupIds?.length ?? 0} uploaded recipients`
  }

  return (
    <Container>
      <Section size="2">
        <Flex direction="column" gap="4">
          <Text size="5" weight="bold">
            Send Push Notification
          </Text>

          <Form.Root
            onSubmit={(e) => {
              e.preventDefault()
              void onReviewSend()
            }}>
            <Flex direction="column" gap="4">
              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium">
                  Category
                </Text>
                <Select.Root value={category} onValueChange={setCategory}>
                  <Select.Trigger placeholder="Select a category" />
                  <Select.Content>
                    {(categoriesResp?.categories ?? []).map((c) => (
                      <Select.Item key={c.key} value={c.key}>
                        {c.displayName}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>

              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium">
                  Title
                </Text>
                <TextField.Root
                  maxLength={TITLE_MAX}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Flex>

              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium">
                  Body
                </Text>
                <TextArea
                  maxLength={BODY_MAX}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </Flex>

              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">
                  Metadata (optional)
                </Text>
                {metadataRows.map((row) => (
                  <Flex key={row.id} gap="2" align="center">
                    <TextField.Root
                      placeholder="key"
                      value={row.key}
                      onChange={(e) => updateRow(row.id, { key: e.target.value })}
                    />
                    <TextField.Root
                      placeholder="value"
                      value={row.value}
                      onChange={(e) => updateRow(row.id, { value: e.target.value })}
                    />
                    <IconButton
                      type="button"
                      variant="soft"
                      color="red"
                      aria-label="Remove row"
                      onClick={() =>
                        setMetadataRows((rows) => rows.filter((r) => r.id !== row.id))
                      }>
                      <TrashIcon />
                    </IconButton>
                  </Flex>
                ))}
                <Button
                  type="button"
                  variant="soft"
                  onClick={() =>
                    setMetadataRows((rows) => [...rows, { id: uuidv4(), key: '', value: '' }])
                  }>
                  Add metadata
                </Button>
              </Flex>

              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">
                  Audience
                </Text>
                <RadioGroup.Root
                  value={audienceMode}
                  onValueChange={(v) => setAudienceMode(v as AudienceMode)}>
                  <RadioGroup.Item value={AudienceMode.ALL}>All users</RadioGroup.Item>
                  <RadioGroup.Item value={AudienceMode.IOS}>iOS users</RadioGroup.Item>
                  <RadioGroup.Item value={AudienceMode.ANDROID}>Android users</RadioGroup.Item>
                  <RadioGroup.Item value={AudienceMode.CSV}>Upload CSV</RadioGroup.Item>
                </RadioGroup.Root>
              </Flex>

              {audienceMode === AudienceMode.CSV ? (
                <Flex direction="column" gap="1">
                  <FileUploader
                    onFileDropped={(file) => setCsvFile(file)}
                    formLabel="Player lookup ID CSV"
                    acceptTypes={{ 'text/csv': ['.csv'] }}
                    showImageAsPreview={false}
                    errorMessage={null}
                  />
                  <Text size="1" color="gray">
                    One player lookup ID per line, with no header row. Every non-empty line is
                    treated as a lookup ID.
                  </Text>
                </Flex>
              ) : null}

              <Form.Submit asChild>
                <Button disabled={isPending} loading={isPending}>
                  Review &amp; Send
                </Button>
              </Form.Submit>

              {formError ? (
                <Callout.Root color="red">
                  <Callout.Text>{formError}</Callout.Text>
                </Callout.Root>
              ) : null}
              {error ? (
                <Callout.Root color="red">
                  <Callout.Text>Send failed: {error.message}</Callout.Text>
                </Callout.Root>
              ) : null}
              {result ? (
                <Callout.Root color="green">
                  <Callout.Text>
                    Queued {result.queuedCount}, skipped {result.notFoundCount} not found,{' '}
                    {result.noDeviceCount} no device.
                  </Callout.Text>
                </Callout.Root>
              ) : null}
            </Flex>
          </Form.Root>

          <AlertDialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialog.Content maxWidth="450px">
              <AlertDialog.Title>Confirm bulk send</AlertDialog.Title>
              <AlertDialog.Description>
                This will send a push notification to {audienceLabel()}. This cannot be undone.
              </AlertDialog.Description>
              <Flex gap="3" mt="4" justify="end">
                <AlertDialog.Cancel>
                  <Button variant="soft" color="gray">
                    Cancel
                  </Button>
                </AlertDialog.Cancel>
                <Button
                  color="red"
                  onClick={() => {
                    if (pendingRequest) {
                      mutate(pendingRequest)
                    }
                    setConfirmOpen(false)
                  }}>
                  Send
                </Button>
              </Flex>
            </AlertDialog.Content>
          </AlertDialog.Root>
        </Flex>
      </Section>
    </Container>
  )
}
